import { useCallback, useState } from "react";
import { useAutoRefresh } from "@/lib/useAutoRefresh";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { daysSince, fmtDateTime } from "@/lib/status";
import PageHeader from "@/components/PageHeader";
import AdvanceButton from "@/components/AdvanceButton";
import MoveToMaintenanceButton from "@/components/MoveToMaintenanceButton";
import CheckOutButton from "@/components/CheckOutButton";
import StatusBadge, { PriorityBadge, TypeBadge } from "@/components/StatusBadge";
import { Layers, Truck, LogOut } from "lucide-react";
import { toast } from "sonner";

// Daily rack-clearing view: what's cut and sitting in the rework rack, plus what was finished recently and needs to go out with its job.
export default function Rack() {
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/reworks");
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    }
  }, []);

  useAutoRefresh(load);

  const inRack = rows.filter((r) => r.status === "In Rework Rack");
  const finished = rows.filter((r) => r.status === "Completed - Finished Rack" && daysSince(r.date_completed) <= 5);
  const collected = rows.filter((r) => r.status === "Collected" && daysSince(r.collected?.at || r.date_completed) <= 5);
  const byJob = (list) => Object.values(list.reduce((acc, r) => { (acc[r.job_number] ||= { job: r.job_number, items: [] }).items.push(r); return acc; }, {}));

  const Row = ({ r, done }) => (
    <div className="flex items-center gap-4 px-4 py-3 border-b border-[#e6e5df] last:border-0" data-testid={`rack-row-${r.id}`}>
      <div className="min-w-0 flex-1">
        <Link to={`/rework/${r.id}`} className="mono text-sm text-[#232826] font-medium hover:text-[#647265]">{r.part_number}</Link>
        <div className="text-xs text-[#6b6a68] truncate">{r.rework_reason} · {r.description}</div>
      </div>
      <PriorityBadge priority={r.priority} />
      <TypeBadge type={r.request_type} />
      {done && r.bay_number ? (
        <div className="w-20 text-right font-mono text-sm font-medium text-[#232826] whitespace-nowrap" data-testid={`rack-bay-${r.id}`}>Bay {r.bay_number}</div>
      ) : (
        <div className="w-16 text-right text-[11px] font-mono text-[#a39f98]">
          {done ? `${daysSince(r.date_completed)}d ago` : `${daysSince(r.status_changed_at)}d`}
        </div>
      )}
      {!done && <AdvanceButton rework={r} onDone={(d) => setRows((rs) => rs.map((x) => (x.id === d.id ? d : x)))} />}
      {done && <CheckOutButton rework={r} onDone={(d) => setRows((rs) => rs.map((x) => (x.id === d.id ? d : x)))} />}
      {done && <MoveToMaintenanceButton rework={r} onDone={(d) => setRows((rs) => rs.map((x) => (x.id === d.id ? d : x)))} />}
    </div>
  );

  return (
    <div>
      <PageHeader title="Rack Clearing" subtitle="Clear this daily. Catch kitchens before they go out." testid="rack-header" />
      <div className="p-6 md:p-10 space-y-6">
       <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <section className="border border-[#e6e5df] bg-white rounded-sm" data-testid="rack-in-rack">
          <div className="px-5 py-4 border-b border-[#e6e5df] flex items-center gap-3">
            <Layers size={16} className="text-indigo-600" />
            <div className="font-display font-bold text-[#232826]">In Rework Rack</div>
            <span className="ml-auto num text-sm text-[#424e43]" data-testid="rack-in-rack-count">{inRack.length}</span>
          </div>
          {inRack.length === 0 && <div className="p-8 text-sm text-[#a39f98] text-center">Rack is clear.</div>}
          {byJob(inRack).map((g) => (
            <div key={g.job}>
              <div className="px-4 py-2 bg-[#efece5] flex items-center justify-between">
                <span className="mono text-sm font-semibold text-[#232826]">{g.job}</span>
                <span className="text-[10px] uppercase tracking-widest text-[#6b6a68]">{g.items.length} part{g.items.length !== 1 ? "s" : ""}</span>
              </div>
              {g.items.map((r) => <Row key={r.id} r={r} />)}
            </div>
          ))}
        </section>

        <section className="border border-[#e6e5df] bg-white rounded-sm" data-testid="rack-finished">
          <div className="px-5 py-4 border-b border-[#e6e5df] flex items-center gap-3">
            <Truck size={16} className="text-emerald-600" />
            <div className="font-display font-bold text-[#232826]">Finished Rack — last 5 days</div>
            <span className="ml-auto num text-sm text-[#424e43]">{finished.length}</span>
          </div>
          {finished.length === 0 && <div className="p-8 text-sm text-[#a39f98] text-center">Nothing finished recently.</div>}
          {byJob(finished).map((g) => (
            <div key={g.job}>
              <div className="px-4 py-2 bg-[#efece5] flex items-center justify-between">
                <span className="mono text-sm font-semibold text-[#232826]">{g.job}</span>
                <StatusBadge status="Completed - Finished Rack" />
              </div>
              {g.items.map((r) => <Row key={r.id} r={r} done />)}
            </div>
          ))}
        </section>
       </div>

        <section className="border border-[#e6e5df] bg-white rounded-sm" data-testid="rack-collected">
          <div className="px-5 py-4 border-b border-[#e6e5df] flex items-center gap-3">
            <LogOut size={16} className="text-teal-600" />
            <div className="font-display font-bold text-[#232826]">Checked out of rack — last 5 days</div>
            <span className="ml-auto num text-sm text-[#424e43]" data-testid="rack-collected-count">{collected.length}</span>
          </div>
          {collected.length === 0 && <div className="p-8 text-sm text-[#a39f98] text-center">No parts checked out recently.</div>}
          {byJob(collected).map((g) => (
            <div key={g.job}>
              <div className="px-4 py-2 bg-[#efece5] flex items-center justify-between">
                <span className="mono text-sm font-semibold text-[#232826]">{g.job}</span>
                <StatusBadge status="Collected" />
              </div>
              {g.items.map((r) => (
                <div key={r.id} className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-[#e6e5df] last:border-0" data-testid={`collected-row-${r.id}`}>
                  <div className="min-w-0 flex-1 basis-40">
                    <Link to={`/rework/${r.id}`} className="mono text-sm text-[#232826] font-medium hover:text-[#647265]">{r.part_number}</Link>
                    <div className="text-xs text-[#6b6a68] truncate">{r.rework_reason} · {r.description}</div>
                  </div>
                  <div className="text-right text-xs text-[#6b6a68]" data-testid={`collected-by-${r.id}`}>
                    <div className="font-medium text-[#232826]">{r.collected?.by}</div>
                    <div className="font-mono text-[11px] text-[#a39f98]">{fmtDateTime(r.collected?.at)}</div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}
